<template>
  <section class="hero">
    <div class="col-left">
      <div class="badge">
        <span class="pill">{{ trans.get('landing.hero.badge_pill') }}</span>
        {{ trans.get('landing.hero.badge_text') }}
        <span class="live"></span>
      </div>

      <h1>
        <span
          v-for="(w, idx) in titleWords"
          :key="idx"
          class="word"
          :class="'d' + (idx + 1)"
        >
          <span :class="{ grad: w.grad }">{{ w.text }}</span>
        </span>
      </h1>

      <p class="lede">
        {{ trans.get('landing.hero.lede_prefix') }}
        <strong class="ink">{{ trans.get('landing.hero.lede_brand') }}</strong>
        {{ trans.get('landing.hero.lede_suffix') }}
      </p>

      <div class="ctas">
        <button
          class="btn-primary"
          type="button"
          data-cta-location="hero"
          @click="openSignup"
        >
          {{ trans.get('landing.hero.cta_register') }}
          <span class="arrow">→</span>
        </button>
        <div class="stores">
          <a
            :href="playStoreUrl"
            target="_blank"
            rel="noopener"
            class="btn-primary btn-primary--outline"
            data-ga-app-store
            data-cta-location="hero"
            :aria-label="trans.get('landing.common.open_google_play_aria')"
          >
            <icon-google-play />
            <span>{{ trans.get('landing.hero.app_cta') }}</span>
            <span class="arrow">→</span>
          </a>
          <div
            class="app-proof"
            :aria-label="trans.get('landing.hero.app_proof_aria')"
          >
            <div
              class="ap-stars"
              aria-hidden="true"
            >
              <icon-star-filled
                v-for="i in 5"
                :key="i"
                class="ap-star"
              />
            </div>
            <div class="ap-text">
              <strong>{{ trans.get('landing.hero.app_proof_num') }}</strong>
              <span>{{ trans.get('landing.hero.app_proof_lbl') }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="trust">
        <div class="stack">
          <div class="av a1"></div>
          <div class="av a2"></div>
          <div class="av a3"></div>
          <div class="av a4"></div>
          <div class="av a5"></div>
          <div class="av a6"></div>
        </div>
        <div class="trust-text">
          <div class="stars">
            <icon-star-outline
              v-for="i in 5"
              :key="i"
              :size="14"
              class="star-thin"
            />
            &nbsp;<strong class="ink">{{ trans.get('landing.hero.trust_rating') }}</strong> ·
            {{ trans.get('landing.hero.trust_reviews') }}
          </div>
          <div>
            <strong>{{ trans.get('landing.hero.trust_subline_strong') }}</strong
            >{{ trans.get('landing.hero.trust_subline_suffix') }}
          </div>
        </div>
      </div>
    </div>

    <div class="stage">
      <div class="float-card fc-match">
        <div class="heart-bubble">
          <picture>
            <source
              type="image/webp"
              srcset="../../../../../images/viluu/landing-v2/profiles/lena-3.webp"
            />
            <img
              src="../../../../../images/viluu/landing-v2/profiles/lena-3.png"
              :alt="trans.get('landing.hero.fc_match_avatar_alt')"
              loading="lazy"
              decoding="async"
              width="240"
              height="240"
            />
          </picture>
        </div>
        <div>
          <div class="ttl">{{ trans.get('landing.hero.fc_match_ttl') }}</div>
          <div class="sub">{{ trans.get('landing.hero.fc_match_sub') }}</div>
        </div>
      </div>

      <div class="float-card fc-coin">
        <div
          class="gift-burst"
          aria-hidden="true"
        >
          <div class="h h1"><icon-heart /></div>
          <div class="h h2"><icon-heart /></div>
          <div class="h h3"><icon-heart /></div>
          <div class="box"></div>
          <div class="bow"></div>
        </div>
        <div>
          <div class="fc-coin-ttl">{{ trans.get('landing.hero.fc_gift_ttl') }}</div>
          <div class="fc-coin-sub">{{ trans.get('landing.hero.fc_gift_sub') }}</div>
        </div>
      </div>

      <div class="float-card fc-near">
        <div class="fc-row">
          <span class="dot-g"></span
          ><span class="ttl">{{ trans.get('landing.hero.fc_near_ttl') }}</span>
        </div>
        <div class="nums">{{ trans.get('landing.hero.fc_near_num') }}</div>
        <div class="lbl">{{ trans.get('landing.hero.fc_near_lbl') }}</div>
      </div>

      <div class="float-card fc-swipe">
        <div class="mini-stack">
          <div class="pcard c1">
            <div class="face"></div>
            <span class="name">Mia</span>
          </div>
          <div class="pcard c2">
            <div class="face"></div>
            <span class="name">Nora</span>
          </div>
          <div class="pcard c3">
            <div class="face"></div>
            <span class="name">Sara</span>
          </div>
        </div>
        <div class="actions-mini">
          <span class="am no">✕</span>
          <span class="am yes"
            ><icon-heart-filled
              :width="9"
              :height="8"
              class="am-heart"
          /></span>
        </div>
        <div class="ttl ttl--top">{{ trans.get('landing.hero.fc_swipe_ttl') }}</div>
      </div>

      <div class="phone">
        <div class="screen">
          <div class="notch"></div>
          <div class="status">
            <span>9:41</span>
            <span class="ico"
              ><icon-signal-bars /><span class="lbl-5g">5G</span><icon-battery
            /></span>
          </div>

          <div class="chat-head">
            <div class="av av--lena"></div>
            <div>
              <div class="name">{{ trans.get('landing.hero.phone_chat_name') }}</div>
              <div class="meta">
                <span class="status-dot"></span>&nbsp;{{
                  trans.get('landing.hero.phone_chat_meta')
                }}
              </div>
            </div>
            <div class="actions">
              <span><icon-dots3-horizontal :size="14" /></span>
            </div>
          </div>

          <div class="chat">
            <div class="bubble them b1">{{ trans.get('landing.hero.phone_bubble_1') }}</div>
            <div class="bubble me b2">{{ trans.get('landing.hero.phone_bubble_2') }}</div>
            <div class="bubble them b3">{{ trans.get('landing.hero.phone_bubble_3') }}</div>
            <div class="bubble me b4">{{ trans.get('landing.hero.phone_bubble_4') }}</div>
            <div class="typing"><span></span><span></span><span></span></div>
          </div>

          <div class="quiz">
            <span class="tag">{{ trans.get('landing.hero.phone_quiz_tag') }}</span>
            <h3>{{ trans.get('landing.hero.phone_quiz_q') }}</h3>
            <div class="opts">
              <div class="opt">{{ trans.get('landing.hero.phone_quiz_a') }}</div>
              <div class="opt sel">{{ trans.get('landing.hero.phone_quiz_b') }}</div>
              <div class="opt">{{ trans.get('landing.hero.phone_quiz_c') }}</div>
            </div>
          </div>

          <div class="input">
            <span class="ico-smile"
              ><icon-smile
                :size="18"
                :stroke-width="1.8"
            /></span>
            <span class="field">{{ trans.get('landing.hero.phone_input_ph') }}</span>
            <span class="ico-row"
              ><icon-camera
                :size="18"
                :stroke-width="1.8" />
              <icon-gallery
                :size="18"
                :stroke-width="1.8"
            /></span>
            <span class="send"><icon-send-plane /></span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const TITLE_WORDS = [
  'title_w1',
  'title_w2',
  'title_w3',
  'title_w4',
  'title_w5',
  'title_w6',
  'title_w7',
];
const GRAD_WORD_INDEX = 5; // 6th word renders with the gradient italic style

import IconGooglePlay from '../icons/IconGooglePlay.vue';
import IconStarFilled from '../icons/IconStarFilled.vue';
import IconStarOutline from '../icons/IconStarOutline.vue';
import IconHeart from '../icons/IconHeart.vue';
import IconHeartFilled from '../icons/IconHeartFilled.vue';
import IconSignalBars from '../icons/IconSignalBars.vue';
import IconBattery from '../icons/IconBattery.vue';
import IconDots3Horizontal from '../icons/IconDots3Horizontal.vue';
import IconSmile from '../icons/IconSmile.vue';
import IconCamera from '../icons/IconCamera.vue';
import IconGallery from '../icons/IconGallery.vue';
import IconSendPlane from '../icons/IconSendPlane.vue';
import { buildPlayStoreUrl } from '../../../analytics/ga4';

export default {
  name: 'HeroSection',
  components: {
    IconGooglePlay,
    IconStarFilled,
    IconStarOutline,
    IconHeart,
    IconHeartFilled,
    IconSignalBars,
    IconBattery,
    IconDots3Horizontal,
    IconSmile,
    IconCamera,
    IconGallery,
    IconSendPlane,
  },
  computed: {
    titleWords() {
      return TITLE_WORDS.map((suffix, idx) => ({
        text: this.trans.get(`landing.hero.${suffix}`),
        grad: idx === GRAD_WORD_INDEX,
      }));
    },
    playStoreUrl() {
      return buildPlayStoreUrl('hero');
    },
  },
  methods: {
    openSignup() {
      this.$eventHub.$emit('viluu-landing:open-signup', {
        mode: 'register',
        cta_location: 'hero',
      });
    },
  },
};
</script>
<style scoped lang="scss">
.hero {
  position: relative;
  z-index: 5;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 60px;
  align-items: center;
  padding: 40px 56px 80px;
  max-width: 1480px;
  margin: 0 auto;
  min-height: calc(100vh - 90px);
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 8px 16px 8px 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  box-shadow: 0 4px 14px -4px rgba(26, 12, 20, 0.08);
  opacity: 0;
  transform: translateY(10px);
  animation: fadeUp 0.55s ease 0.05s forwards;
  cursor: default;
  user-select: none;
}

.badge .pill {
  background: linear-gradient(135deg, var(--pink), var(--coral));
  color: #fff;
  font-weight: 700;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.badge .live {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: pulse 2s infinite;
  margin-left: 4px;
}

h1 {
  font-family: var(--display);
  font-weight: 700;
  font-size: clamp(48px, 7.2vw, 104px);
  line-height: 0.96;
  letter-spacing: -0.035em;
  color: var(--ink);
  margin: 24px 0 24px;
}

.word {
  padding-bottom: 0.08em;
  display: inline-block;
  overflow: hidden;
  vertical-align: bottom;
  /* 0.22em word gap minus the 0.1em right padding on the inner span below, so
     the anti-clip room doesn't widen the spacing between words. */
  margin-right: 0.12em;
}

.word:last-child {
  margin-right: 0;
}

.word > span {
  padding-bottom: 0.08em;
  /* Room on the right so the last glyph (trimmed by the h1's negative
     letter-spacing) isn't cropped by .word's overflow: hidden. The gradient
     word overrides this with its own larger padding from the global .grad. */
  padding-right: 0.1em;
  display: inline-block;
  transform: translateY(110%);
  animation: rise2 0.75s cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
}

.word.d1 > span {
  animation-delay: 0.05s;
}

.word.d2 > span {
  animation-delay: 0.12s;
}

.word.d3 > span {
  animation-delay: 0.19s;
}

.word.d4 > span {
  animation-delay: 0.26s;
}

.word.d5 > span {
  animation-delay: 0.33s;
}

.word.d6 > span {
  animation-delay: 0.4s;
}

.word.d7 > span {
  animation-delay: 0.47s;
}

.lede {
  font-size: 18px;
  line-height: 1.55;
  color: var(--ink-dim);
  max-width: 520px;
  margin: 0 0 36px;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeUp 0.65s ease 0.45s forwards;
}

.lede strong {
  color: var(--ink);
  font-weight: 700;
}

.ctas {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeUp 0.65s ease 0.57s forwards;
}

.trust {
  margin-top: 48px;
  display: flex;
  align-items: center;
  gap: 20px;
  opacity: 0;
  animation: fadeUp 0.65s ease 0.68s forwards;
}

.stage {
  position: relative;
  height: 680px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.phone {
  position: relative;
  width: 320px;
  height: 640px;
  background: #1a0c14;
  border-radius: 46px;
  padding: 14px;
  box-shadow: 0 40px 80px -20px rgba(255, 59, 127, 0.35), 0 20px 40px -10px rgba(26, 12, 20, 0.25),
    inset 0 0 0 2px rgba(255, 255, 255, 0.05);
  transform: rotate(-4deg) translateY(0);
  animation: phoneIn 0.9s cubic-bezier(0.2, 0.7, 0.2, 1) 0.2s both,
    phoneFloat 6s ease-in-out 1.5s infinite;
  z-index: 3;
}

.phone .screen {
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #fff5f3, #ffe9e3);
  border-radius: 34px;
  overflow: hidden;
}

.notch {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 96px;
  height: 24px;
  background: #1a0c14;
  border-radius: 14px;
  z-index: 5;
}

.status {
  position: absolute;
  top: 14px;
  left: 24px;
  right: 24px;
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 600;
  color: var(--ink);
  z-index: 4;
}

.status .ico {
  display: inline-flex;
  gap: 3px;
  align-items: center;
}

.chat-head {
  position: absolute;
  top: 48px;
  left: 0;
  right: 0;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--line);
}

.chat-head .av {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff9a8b, #ff6a88);
  position: relative;
}

.chat-head .av::after {
  content: '';
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid #fff;
}

.chat-head .name {
  font-weight: 700;
  font-size: 14px;
  color: var(--ink);
  line-height: 1.1;
}

.chat-head .meta {
  font-size: 11px;
  color: var(--ink-dim);
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
}

.chat-head .actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
  color: var(--ink-dim);
}

.chat-head .actions span {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: rgba(255, 59, 127, 0.08);
}

.chat {
  position: absolute;
  top: 128px;
  left: 0;
  right: 0;
  bottom: 88px;
  padding: 14px 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bubble {
  max-width: 78%;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.35;
  border-radius: 18px;
  opacity: 0;
  transform: translateY(8px);
  animation: bubbleIn 0.5s ease forwards;
}

.bubble.them {
  align-self: flex-start;
  background: #fff;
  color: var(--ink);
  border-bottom-left-radius: 6px;
  box-shadow: 0 4px 10px -4px rgba(26, 12, 20, 0.08);
}

.bubble.me {
  align-self: flex-end;
  background: linear-gradient(135deg, var(--pink), var(--coral));
  color: #fff;
  border-bottom-right-radius: 6px;
  box-shadow: 0 6px 14px -4px rgba(255, 59, 127, 0.45);
}

.bubble.b1 {
  animation-delay: 1.2s;
}

.bubble.b2 {
  animation-delay: 1.7s;
}

.bubble.b3 {
  animation-delay: 2.4s;
}

.bubble.b4 {
  animation-delay: 3.1s;
}

.typing {
  align-self: flex-start;
  background: #fff;
  padding: 12px 14px;
  border-radius: 18px;
  border-bottom-left-radius: 6px;
  display: inline-flex;
  gap: 4px;
  box-shadow: 0 4px 10px -4px rgba(26, 12, 20, 0.08);
  opacity: 0;
  animation: bubbleIn 0.4s ease 3.6s forwards;
}

.typing span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ink-mute);
  animation: typing 1.2s ease-in-out infinite;
}

.typing span:nth-child(2) {
  animation-delay: 0.15s;
}

.typing span:nth-child(3) {
  animation-delay: 0.3s;
}

.quiz {
  position: absolute;
  left: 50%;
  top: 42%;
  transform: translate(-50%, -50%) scale(0.9);
  width: 78%;
  background: #fff;
  border-radius: 24px;
  padding: 18px 18px 16px;
  box-shadow: 0 20px 40px -12px rgba(255, 59, 127, 0.45);
  opacity: 0;
  animation: quizIn 0.6s cubic-bezier(0.2, 0.7, 0.2, 1) 0.55s forwards;
  z-index: 6;
}

.quiz .tag {
  display: inline-block;
  background: linear-gradient(135deg, var(--pink), var(--coral));
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.quiz h3 {
  font-family: var(--display);
  font-size: 18px;
  line-height: 1.2;
  margin: 8px 0 12px;
  color: var(--ink);
  font-weight: 700;
  letter-spacing: -0.01em;
}

.quiz .opts {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.quiz .opt {
  font-size: 12px;
  font-weight: 500;
  padding: 9px 12px;
  border-radius: 12px;
  background: #fff5f3;
  color: var(--ink);
  border: 1.5px solid transparent;
  transition: all 0.2s;
  cursor: pointer;
}

.quiz .opt.sel {
  border-color: var(--pink);
  background: #fff;
  color: var(--pink);
  font-weight: 600;
}

.quiz .opt:hover {
  background: #ffe9e3;
}

.input {
  position: absolute;
  bottom: 14px;
  left: 14px;
  right: 14px;
  background: #fff;
  border-radius: 24px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 6px 14px -6px rgba(26, 12, 20, 0.12);
}

.input .field {
  flex: 1;
  font-size: 12px;
  color: var(--ink-mute);
}

.input .send {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--pink), var(--coral));
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 14px;
}

.input .ico-row {
  display: flex;
  gap: 6px;
  color: var(--ink-mute);
}

.float-card {
  position: absolute;
  background: #fff;
  border-radius: 20px;
  padding: 14px 16px;
  font-size: 13px;
  z-index: 4;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.9) inset, 0 -1px 0 rgba(26, 12, 20, 0.04) inset,
    0 1px 2px rgba(26, 12, 20, 0.06), 0 8px 16px -6px rgba(26, 12, 20, 0.12),
    0 24px 48px -16px rgba(198, 62, 124, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.8);
  backdrop-filter: saturate(1.2);
  opacity: 0;
  animation: popIn 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
  transition: transform 0.35s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.35s ease;
}

.float-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.6), transparent 35%);
}

.float-card:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.9) inset, 0 -1px 0 rgba(26, 12, 20, 0.04) inset,
    0 4px 8px rgba(26, 12, 20, 0.08), 0 14px 24px -8px rgba(26, 12, 20, 0.16),
    0 36px 60px -18px rgba(198, 62, 124, 0.4);
}

.fc-match {
  top: 8%;
  left: -2%;
  display: flex;
  align-items: center;
  gap: 12px;
  animation-delay: 1.4s;
}

.fc-match .heart-bubble {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: radial-gradient(ellipse 80% 50% at 30% 25%, rgba(255, 255, 255, 0.4), transparent 60%),
    linear-gradient(135deg, #f17c08 0%, #db5a3a 35%, #c63e7c 100%);
  box-shadow: inset 0 -3px 6px rgba(120, 20, 50, 0.35), inset 0 2px 3px rgba(255, 255, 255, 0.4),
    0 6px 14px rgba(198, 62, 124, 0.45), 0 2px 4px rgba(241, 124, 8, 0.3);
  display: grid;
  place-items: center;
  flex-shrink: 0;
  position: relative;
}

.fc-match .heart-bubble svg {
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.2));
}

.fc-match .heart-bubble img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  display: block;
}

.fc-match .ttl {
  font-weight: 700;
  color: var(--ink);
  font-size: 13px;
  line-height: 1.1;
}

.fc-match .sub {
  font-size: 11px;
  color: var(--ink-dim);
  margin-top: 3px;
}

.fc-coin {
  top: 30%;
  right: -4%;
  display: flex;
  align-items: center;
  gap: 12px;
  animation-delay: 1.8s;
}

.gift-burst {
  position: relative;
  width: 54px;
  height: 54px;
  flex-shrink: 0;
  overflow: visible;
}

.gift-burst .box {
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 42px;
  height: 34px;
  border-radius: 8px 8px 10px 10px;
  background: linear-gradient(180deg, #f17c08 0%, #db5a3a 50%, #c63e7c 100%);
  box-shadow: 0 8px 16px -4px rgba(198, 62, 124, 0.5), inset 0 -3px 4px rgba(120, 20, 50, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.gift-burst .box::before {
  content: '';
  position: absolute;
  left: 50%;
  top: -2px;
  bottom: -2px;
  width: 6px;
  transform: translateX(-50%);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(255, 235, 225, 0.85));
  box-shadow: inset 0 0 0 1px rgba(120, 20, 50, 0.08);
}

.gift-burst .box::after {
  content: '';
  position: absolute;
  left: -3px;
  right: -3px;
  top: 8px;
  height: 5px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.95), rgba(255, 235, 225, 0.85));
  box-shadow: inset 0 0 0 1px rgba(120, 20, 50, 0.08);
}

.gift-burst .bow {
  position: absolute;
  left: 50%;
  top: 14px;
  transform: translateX(-50%);
  width: 18px;
  height: 8px;
  z-index: 2;
}

.gift-burst .bow::before,
.gift-burst .bow::after {
  content: '';
  position: absolute;
  top: 0;
  width: 9px;
  height: 8px;
  border-radius: 50%;
  background: radial-gradient(ellipse at 30% 30%, #fff 0%, #fcd9c4 60%, #f5a978 100%);
  box-shadow: 0 1px 2px rgba(120, 20, 50, 0.2);
}

.gift-burst .bow::before {
  left: 0;
  transform: rotate(-15deg);
}

.gift-burst .bow::after {
  right: 0;
  transform: rotate(15deg);
}

.gift-burst .h {
  position: absolute;
  left: 50%;
  bottom: 24px;
  width: 10px;
  height: 10px;
  color: var(--pink);
  transform-origin: center bottom;
  animation: giftPop 2.6s ease-in-out infinite;
}

.gift-burst .h svg {
  width: 100%;
  height: 100%;
  display: block;
  filter: drop-shadow(0 2px 4px rgba(198, 62, 124, 0.45));
}

.gift-burst .h.h1 {
  margin-left: -16px;
  animation-delay: 0s;
  color: var(--pink);
}

.gift-burst .h.h2 {
  margin-left: -5px;
  animation-delay: 0.4s;
  color: var(--coral);
}

.gift-burst .h.h3 {
  margin-left: 6px;
  animation-delay: 0.8s;
  color: var(--pink);
}

.fc-near {
  bottom: 14%;
  left: -6%;
  animation-delay: 2.2s;
}

.fc-near .fc-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fc-near .dot-g {
  display: block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #6affa0, #22c55e 60%, #15803d);
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18), 0 2px 4px rgba(34, 197, 94, 0.4);
}

.fc-near .ttl {
  font-weight: 700;
  font-size: 12px;
  color: var(--ink);
}

.fc-near .nums {
  font-family: var(--display);
  font-weight: 700;
  font-size: 28px;
  background: linear-gradient(135deg, #f17c08, #c63e7c);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  letter-spacing: -0.02em;
  margin-top: 4px;
  line-height: 1;
}

.fc-near .lbl {
  font-size: 10px;
  color: var(--ink-dim);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 4px;
}

.fc-swipe {
  bottom: 6%;
  right: -2%;
  animation-delay: 2.6s;
  padding: 14px 14px 12px;
}

.fc-swipe .mini-stack {
  position: relative;
  width: 104px;
  height: 78px;
  margin-bottom: 10px;
  perspective: 600px;
}

.fc-swipe .pcard {
  position: absolute;
  width: 72px;
  height: 74px;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.5) inset, 0 -1px 0 rgba(26, 12, 20, 0.1) inset,
    0 4px 8px -2px rgba(26, 12, 20, 0.18), 0 12px 24px -6px rgba(26, 12, 20, 0.25);
  border: 1.5px solid #fff;
}

.fc-swipe .pcard::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 50%, rgba(0, 0, 0, 0.35));
}

.fc-swipe .pcard .name {
  position: absolute;
  left: 6px;
  bottom: 5px;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.fc-swipe .pcard .face {
  position: absolute;
  inset: 0;
}

.fc-swipe .c1 {
  left: 0;
  top: 8px;
  transform: rotate(-10deg) translateZ(-20px);
  opacity: 0.85;
}

.fc-swipe .c1 .face {
  background: radial-gradient(
    ellipse 40% 30% at 50% 35%,
    rgba(255, 200, 180, 0.95),
    rgba(255, 154, 139, 1) 50%,
    rgba(220, 100, 90, 1) 100%
  );
}

.fc-swipe .c1 .face::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 30%;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #ffe2cc, #d99a7a);
  transform: translateX(-50%);
}

.fc-swipe .c1 .face::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 55%;
  width: 34px;
  height: 30px;
  border-radius: 50% 50% 16px 16px;
  background: linear-gradient(180deg, #c97a5a, #8b4a2e);
  transform: translateX(-50%);
}

.fc-swipe .c2 {
  left: 14px;
  top: 0;
  transform: rotate(3deg) translateZ(0);
  z-index: 2;
}

.fc-swipe .c2 .face {
  background-image: image-set(
    url('../../../../../images/viluu/landing-v2/profiles/lena-2.webp') type('image/webp'),
    url('../../../../../images/viluu/landing-v2/profiles/lena-2.png')
  );
  background-size: cover;
  background-position: center 25%;
}

.fc-swipe .c2 .face::before,
.fc-swipe .c2 .face::after {
  display: none;
}

.fc-swipe .c3 {
  left: 28px;
  top: 6px;
  transform: rotate(11deg) translateZ(-10px);
  opacity: 0.92;
  z-index: 1;
}

.fc-swipe .c3 .face {
  background: radial-gradient(
    ellipse 40% 30% at 50% 35%,
    rgba(255, 180, 170, 0.95),
    rgba(255, 106, 136, 1) 50%,
    rgba(200, 50, 90, 1) 100%
  );
}

.fc-swipe .c3 .face::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 30%;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #ffe5d8, #c98a72);
  transform: translateX(-50%);
}

.fc-swipe .c3 .face::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 55%;
  width: 34px;
  height: 30px;
  border-radius: 50% 50% 16px 16px;
  background: linear-gradient(180deg, #ff9a8b, #c25656);
  transform: translateX(-50%);
}

.fc-swipe .actions-mini {
  display: flex;
  gap: 6px;
  margin-top: 6px;
  justify-content: center;
}

.fc-swipe .am {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 10px;
  color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.fc-swipe .am.no {
  background: linear-gradient(135deg, #fff, #f3f3f3);
  color: #999;
  border: 1px solid #eee;
}

.fc-swipe .am.yes {
  background: linear-gradient(135deg, #f17c08, #c63e7c);
}

.fc-swipe .ttl {
  font-weight: 700;
  color: var(--ink);
  font-size: 11px;
  text-align: center;
  letter-spacing: -0.01em;
}

.stores {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
}

.app-proof {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  font-family: var(--sans);
  line-height: 1.1;
  color: var(--ink-dim);
}

.app-proof .ap-stars {
  display: flex;
  gap: 1.5px;
}

.app-proof .ap-stars .ap-star {
  width: 13px;
  height: 13px;
  display: block;
  color: #f59e0b;
}

.fc-swipe .am-heart {
  color: #fff;
}

.app-proof .ap-text {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.app-proof .ap-text strong {
  font-family: var(--display);
  font-weight: 700;
  font-size: 15px;
  color: var(--ink);
  letter-spacing: -0.01em;
}

.app-proof .ap-text span {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.hero .btn-primary--outline {
  background: #fff !important;
  border: 1.5px solid var(--line) !important;
  color: var(--ink) !important;
  box-shadow: 0 6px 18px -8px rgba(26, 12, 20, 0.18) !important;
}

.hero .btn-primary--outline:hover {
  border-color: rgba(198, 62, 124, 0.4) !important;
  box-shadow: 0 10px 22px -8px rgba(198, 62, 124, 0.25) !important;
}

.badge,
.word > span,
.lede,
.ctas,
.trust,
.phone {
  animation-play-state: paused;
}

.stage .status .ico .lbl-5g {
  font-size: 10px;
}

.chat-head .meta .status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  display: inline-block;
}

.stage .chat-head .av.av--lena {
  background-image: image-set(
    url('../../../../../images/viluu/landing-v2/profiles/lena.webp') type('image/webp'),
    url('../../../../../images/viluu/landing-v2/profiles/lena.png')
  );
  background-size: cover;
  background-position: center;
}

.fc-coin .fc-coin-ttl {
  font-weight: 700;
  color: var(--ink);
  font-size: 13px;
  line-height: 1.15;
}

.fc-coin .fc-coin-sub {
  font-size: 11px;
  color: var(--ink-dim);
  margin-top: 3px;
}

.fc-swipe .ttl--top {
  margin-top: 6px;
}

.screen .input .ico-smile {
  color: var(--ink-mute);
  display: inline-flex;
}

@media (max-width: 1100px) {
  .hero {
    grid-template-columns: 1fr;
    padding: 30px 28px 60px;
    gap: 50px;
  }
  .stage {
    height: 560px;
  }
}

@media (max-width: 720px) {
  .hero {
    padding: 16px 20px 40px;
    gap: 32px;
    min-height: auto;
  }
  .badge {
    font-size: 12px;
    padding: 6px 14px 6px 6px;
  }
  .badge .pill {
    font-size: 10px;
    padding: 3px 8px;
  }
  h1 {
    font-size: clamp(40px, 11vw, 56px);
    margin: 18px 0 18px;
    line-height: 1;
  }
  .lede {
    font-size: 15px;
    margin: 0 0 24px;
    line-height: 1.5;
  }
  .ctas {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  .stores {
    justify-content: center;
    gap: 8px;
    flex-direction: column;
  }
  .trust {
    margin-top: 32px;
    gap: 11px;
    align-items: center;
  }

  .stage {
    height: auto;
    min-height: 560px;
    padding: 30px 0 20px;
  }
  .phone {
    width: 280px;
    height: 560px;
    border-radius: 38px;
    padding: 10px;
    transform: rotate(-2deg);
  }
  .phone {
    animation: phoneIn 0.9s cubic-bezier(0.2, 0.7, 0.2, 1) 0.2s both,
      phoneFloat 6s ease-in-out 1.5s infinite;
  }
  .phone .screen {
    border-radius: 30px;
  }
  .notch {
    width: 80px;
    height: 20px;
  }

  .fc-match {
    top: -8px;
    left: -4px;
    padding: 8px 12px;
  }
  .fc-match .heart-bubble {
    width: 30px;
    height: 30px;
  }
  .fc-match .ttl {
    font-size: 12px;
  }
  .fc-match .sub {
    font-size: 10px;
  }
  .fc-coin {
    top: auto;
    bottom: 30px;
    right: -4px;
    left: auto;
    padding: 8px 12px;
    transform: none;
    animation-delay: 1.6s;
  }
  .fc-near {
    display: none;
  }
  .fc-swipe {
    display: none;
  }
}

@media (max-width: 380px) {
  .phone {
    width: 260px;
    height: 520px;
  }
  .stage {
    min-height: 520px;
  }
  h1 {
    font-size: 38px;
  }
}

@media (max-width: 560px) {
  .stores {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  .stores .btn-primary--outline {
    width: 100%;
    justify-content: center;
  }
  .app-proof {
    justify-content: center;
  }
}

@keyframes phoneIn {
  from {
    opacity: 0;
    transform: rotate(-4deg) translateY(40px) scale(0.94);
  }

  to {
    opacity: 1;
    transform: rotate(-4deg) translateY(0) scale(1);
  }
}

@keyframes phoneFloat {
  0%,
  100% {
    transform: rotate(-4deg) translateY(0);
  }

  50% {
    transform: rotate(-4deg) translateY(-12px);
  }
}

@keyframes bubbleIn {
  to {
    opacity: 1;
    transform: none;
  }
}


@keyframes quizIn {
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes giftPop {
  0% {
    opacity: 0;
    transform: translate(-50%, 4px) scale(0.3) rotate(-10deg);
  }

  20% {
    opacity: 1;
    transform: translate(-50%, -4px) scale(0.95) rotate(0);
  }

  60% {
    opacity: 1;
    transform: translate(-50%, -22px) scale(1.05) rotate(8deg);
  }

  100% {
    opacity: 0;
    transform: translate(-50%, -36px) scale(0.85) rotate(20deg);
  }
}

@media (max-width: 720px) {
  @keyframes phoneFloat {
    0%,
    100% {
      transform: rotate(-2deg) translateY(0);
    }

    50% {
      transform: rotate(-2deg) translateY(-8px);
    }
  }
  @keyframes phoneIn {
    from {
      opacity: 0;
      transform: rotate(-2deg) translateY(40px) scale(0.94);
    }

    to {
      opacity: 1;
      transform: rotate(-2deg) translateY(0) scale(1);
    }
  }
}
</style>
